import { CategoryNodeId } from '../category/category-node-id';
import { BrandSlug } from '../shared/brand-slug';
import { EAN } from '../shared/ean';

export type BrandInCondition = {
  readonly kind: 'brand-in';
  readonly slugs: readonly BrandSlug[];
};

export type EanInCondition = {
  readonly kind: 'ean-in';
  readonly eans: readonly EAN[];
};

export type CategoryUnderCondition = {
  readonly kind: 'category-under';
  readonly nodeId: CategoryNodeId;
};

export type CategoryIsCondition = {
  readonly kind: 'category-is';
  readonly value: string;
};

export type TagHasAnyCondition = {
  readonly kind: 'tag-has-any';
  readonly tags: readonly string[];
};

export type TagHasAllCondition = {
  readonly kind: 'tag-has-all';
  readonly tags: readonly string[];
};

export type PclassInCondition = {
  readonly kind: 'pclass-in';
  readonly pclasses: readonly string[];
};

export type PclassNotInCondition = {
  readonly kind: 'pclass-not-in';
  readonly pclasses: readonly string[];
};

export type PriceLteCondition = {
  readonly kind: 'price-lte';
  readonly value: number;
};

export type PriceGteCondition = {
  readonly kind: 'price-gte';
  readonly value: number;
};

export type PriceBetweenCondition = {
  readonly kind: 'price-between';
  readonly lo: number;
  readonly hi: number;
};

export type Condition =
  | BrandInCondition
  | EanInCondition
  | CategoryUnderCondition
  | CategoryIsCondition
  | TagHasAnyCondition
  | TagHasAllCondition
  | PclassInCondition
  | PclassNotInCondition
  | PriceLteCondition
  | PriceGteCondition
  | PriceBetweenCondition;

export type SkuPredicate =
  | { readonly kind: 'all'; readonly predicates: readonly SkuPredicate[] }
  | { readonly kind: 'any'; readonly predicates: readonly SkuPredicate[] }
  | { readonly kind: 'not'; readonly predicate: SkuPredicate }
  | Condition
  | boolean;

export namespace SkuPredicate {
  /** Brands by identity. No slugs selects nothing: the empty brand-in is not a valid condition. */
  export function brandIn(slugs: readonly BrandSlug[]): SkuPredicate {
    return slugs.length > 0 ? { kind: 'brand-in', slugs: [...slugs] } : false;
  }

  export function categoryUnder(nodeId: CategoryNodeId): SkuPredicate {
    return { kind: 'category-under', nodeId };
  }

  /**
   * One category by the catalog's own value, exactly. Not the subtree beneath it and not a
   * second column: the products whose category IS this value. The value is a catalog
   * category as the catalog spells it, so it needs no node map to resolve -- which is why
   * this condition, unlike category-under, can name a category the node map does not know.
   */
  export function categoryIs(value: string): SkuPredicate {
    const trimmed = value.trim();
    if (!trimmed) {
      throw new Error(`SkuPredicate.categoryIs: value must be non-empty (got: "${value}")`);
    }
    return { kind: 'category-is', value: trimmed };
  }

  /**
   * Products by identity, for a universe already resolved to codes -- a narrowing no other
   * condition expresses, such as one product page of a brand. No codes selects nothing, like
   * the other by-identity conditions.
   */
  export function eanIn(eans: readonly EAN[]): SkuPredicate {
    return eans.length > 0 ? { kind: 'ean-in', eans: [...eans] } : false;
  }

  /** Tags by name. No tags selects nothing: the empty tag-has-any is not a valid condition. */
  export function tagHasAny(tags: readonly string[]): SkuPredicate {
    const trimmed = tags.map((t) => t.trim());
    const empty = trimmed.findIndex((t) => !t);
    if (empty !== -1) {
      throw new Error(`SkuPredicate.tagHasAny: tag must be non-empty (got: "${tags[empty]}")`);
    }
    return trimmed.length > 0 ? { kind: 'tag-has-any', tags: trimmed } : false;
  }

  /**
   * Brands the predicate names itself. Anything other than a bare brand-in names none, even
   * though its members have brands -- those require resolving the members.
   */
  export function brandSlugs(predicate: SkuPredicate): readonly BrandSlug[] {
    if (predicate === false || predicate === true) return [];
    if (predicate.kind === 'brand-in') return [...predicate.slugs];
    return [];
  }
}

// Parse-don't-validate factory. Accepts external (unknown) input and returns a
// validated SkuPredicate, throwing with an informative message on any invalid shape.
export function parseSpec(raw: unknown): SkuPredicate {
  if (raw === true || raw === false) return raw;
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    throw new Error(`Invalid predicate: expected object or boolean, got ${JSON.stringify(raw)}`);
  }
  const obj = raw as Record<string, unknown>;
  if (!('kind' in obj)) {
    throw new Error(
      `Invalid predicate: no 'kind' discriminant. Keys present: ${Object.keys(obj).join(', ') || '(none)'}`
    );
  }
  return parseByKind(obj);
}

function parseByKind(obj: Record<string, unknown>): SkuPredicate {
  const { kind } = obj;
  switch (kind) {
    case 'all': {
      const predicates = obj['predicates'];
      if (!Array.isArray(predicates)) {
        throw new Error(`Invalid 'all' predicate: 'predicates' must be an array`);
      }
      return { kind: 'all', predicates: predicates.map(parseSpec) };
    }
    case 'any': {
      const predicates = obj['predicates'];
      if (!Array.isArray(predicates)) {
        throw new Error(`Invalid 'any' predicate: 'predicates' must be an array`);
      }
      return { kind: 'any', predicates: predicates.map(parseSpec) };
    }
    case 'not': {
      return { kind: 'not', predicate: parseSpec(obj['predicate']) };
    }
    case 'brand-in': {
      const slugs = obj['slugs'];
      if (!Array.isArray(slugs) || slugs.length === 0) {
        throw new Error(`Invalid 'brand-in' condition: 'slugs' must be a non-empty array`);
      }
      return { kind: 'brand-in', slugs: slugs.map((s) => BrandSlug(String(s))) };
    }
    case 'ean-in': {
      const eans = obj['eans'];
      if (!Array.isArray(eans) || eans.length === 0) {
        throw new Error(`Invalid 'ean-in' condition: 'eans' must be a non-empty array`);
      }
      return { kind: 'ean-in', eans: eans.map((e) => EAN(String(e))) };
    }
    case 'category-under': {
      const nodeId = obj['nodeId'];
      if (typeof nodeId !== 'string') {
        throw new Error(
          `Invalid 'category-under' condition: 'nodeId' must be a string, got ${JSON.stringify(nodeId)}`
        );
      }
      return { kind: 'category-under', nodeId: CategoryNodeId(nodeId) };
    }
    case 'tag-has-any': {
      const tags = obj['tags'];
      if (!Array.isArray(tags) || tags.length === 0) {
        throw new Error(`Invalid 'tag-has-any' condition: 'tags' must be a non-empty array`);
      }
      return { kind: 'tag-has-any', tags: tags.map(String) };
    }
    case 'tag-has-all': {
      const tags = obj['tags'];
      if (!Array.isArray(tags) || tags.length === 0) {
        throw new Error(`Invalid 'tag-has-all' condition: 'tags' must be a non-empty array`);
      }
      return { kind: 'tag-has-all', tags: tags.map(String) };
    }
    case 'pclass-in': {
      const pclasses = obj['pclasses'];
      if (!Array.isArray(pclasses) || pclasses.length === 0) {
        throw new Error(`Invalid 'pclass-in' condition: 'pclasses' must be a non-empty array`);
      }
      return { kind: 'pclass-in', pclasses: pclasses.map(String) };
    }
    case 'pclass-not-in': {
      const pclasses = obj['pclasses'];
      if (!Array.isArray(pclasses) || pclasses.length === 0) {
        throw new Error(`Invalid 'pclass-not-in' condition: 'pclasses' must be a non-empty array`);
      }
      return { kind: 'pclass-not-in', pclasses: pclasses.map(String) };
    }
    case 'price-lte': {
      const value = obj['value'];
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        throw new Error(
          `Invalid 'price-lte' condition: 'value' must be a finite number, got ${JSON.stringify(value)}`
        );
      }
      return { kind: 'price-lte', value };
    }
    case 'price-gte': {
      const value = obj['value'];
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        throw new Error(
          `Invalid 'price-gte' condition: 'value' must be a finite number, got ${JSON.stringify(value)}`
        );
      }
      return { kind: 'price-gte', value };
    }
    case 'price-between': {
      const lo = obj['lo'];
      const hi = obj['hi'];
      if (typeof lo !== 'number' || !Number.isFinite(lo)) {
        throw new Error(
          `Invalid 'price-between' condition: 'lo' must be a finite number, got ${JSON.stringify(lo)}`
        );
      }
      if (typeof hi !== 'number' || !Number.isFinite(hi)) {
        throw new Error(
          `Invalid 'price-between' condition: 'hi' must be a finite number, got ${JSON.stringify(hi)}`
        );
      }
      if (lo > hi) {
        throw new Error(
          `Invalid 'price-between' condition: 'lo' (${lo}) must not exceed 'hi' (${hi})`
        );
      }
      return { kind: 'price-between', lo, hi };
    }
    default:
      throw new Error(`Unknown condition kind: "${kind}"`);
  }
}
